/** Exports and reports: the job list, create and cancel through a dialog, and a download that re-checks (U096-U098, UX11). */
import {
  decodeExportJobRow,
  FIDELITIES,
  JOBS_PATH,
  JOB_KINDS,
  type ExportJobRow,
} from "../../contracts/delivery.ts";
import { MODES } from "../../contracts/envelope.ts";
import { openConfirmDialog, type DialogHandle } from "../../app/command-dialog-view.ts";
import {
  badge,
  instantCell,
  metric,
  renderFreshnessBar,
  scrollRegion,
} from "../../app/components.ts";
import { h, setChildren } from "../../app/dom.ts";
import { formatQuantity } from "../../app/format.ts";
import { renderStateBanner, stateOfEnvelope, stateOfError } from "../../app/state.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { encodeExportRequest } from "../../contracts/delivery.ts";
import { jobPresentation, refusalMessage, sha256Hex } from "../delivery/model.ts";
import { servicesOf, type DeliveryServices } from "../delivery/services.ts";
import { handleOf, makeFrame } from "../frame.ts";
import { buildExportRequest, formProblem, REVIEW_PERIODS, type ExportForm } from "./form.ts";
import "../../styles/delivery.css";

const LIVE: readonly string[] = ["QUEUED", "RUNNING", "CANCEL_REQUESTED"];
const POLL_MS = 3000;

function select(
  name: string,
  label: string,
  values: readonly string[],
  chosen: string,
): HTMLLabelElement {
  return h(
    "label",
    {},
    `${label} `,
    h(
      "select",
      { attrs: { name } },
      ...values.map((value) => {
        const option = h("option", { attrs: { value } }, value === "" ? "Choose…" : value);
        option.selected = value === chosen;
        return option;
      }),
    ),
  );
}

function input(name: string, label: string, value: string): HTMLLabelElement {
  return h(
    "label",
    {},
    `${label} `,
    h("input", { attrs: { name, type: "text", value, autocomplete: "off" } }),
  );
}

/** Mount the exports view. */
export function mountExports(
  ctx: ViewContext,
  services: DeliveryServices = servicesOf(ctx, window),
): ViewHandle {
  const frame = makeFrame("exports", "Exports and reports");
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  let jobs: ExportJobRow[] = [];
  let dataMode = "";
  const status = h("p", {
    class: "download-status",
    attrs: { role: "status", "aria-live": "polite" },
  });
  const list = h("div", { class: "jobs" });
  let dialog: DialogHandle | null = null;
  ctx.scope.add(() => {
    dialog?.close();
  });
  const confirmWith = (opener: HTMLElement, intent: Parameters<typeof openConfirmDialog>[1]) => {
    dialog?.close();
    dialog = openConfirmDialog(ctx.commands.keys, intent, opener);
  };

  const upsert = (row: ExportJobRow): void => {
    jobs = jobs.some((job) => job.jobId === row.jobId)
      ? jobs.map((job) => (job.jobId === row.jobId ? row : job))
      : [row, ...jobs];
    draw();
  };

  const download = async (row: ExportJobRow, chunk: number): Promise<void> => {
    status.dataset.status = "working";
    status.textContent = `Checking your access to ${row.jobId}…`;
    const path = `${JOBS_PATH}/${row.jobId}/download?chunk=${String(chunk)}&mode=${row.mode}&fidelity=${row.fidelity}`;
    try {
      const answer = await services.download(path);
      if (!answer.ok) {
        status.dataset.status = "refused";
        status.textContent = refusalMessage(answer.status, answer.error);
        await refresh();
        return;
      }
      const actual = await sha256Hex(answer.bytes);
      if (answer.sha256 !== actual) {
        status.dataset.status = "refused";
        status.textContent =
          "Download refused: the bytes do not match the server's digest. No file was saved.";
        return;
      }
      services.save(`export-${row.jobId}-${String(chunk)}.csv`, answer.bytes);
      status.dataset.status = "ok";
      status.textContent = `Saved chunk ${String(chunk + 1)} of ${String(answer.chunks ?? "?")} (${formatQuantity(answer.bytes.length)} bytes); digest verified.`;
    } catch {
      status.dataset.status = "refused";
      status.textContent = "Download failed: the server could not be reached. No file was saved.";
    }
  };

  const confirmCancel = (row: ExportJobRow, opener: HTMLElement): void => {
    confirmWith(opener, {
      intentId: `export-cancel|${row.jobId}`,
      id: "cancel-export",
      title: "Cancel this export?",
      summary: [
        ["Job", row.jobId],
        ["State", jobPresentation(row).label],
        [
          "Effect",
          "A running job stops at its next chunk and drops what it wrote; nothing is downloadable.",
        ],
      ],
      confirmLabel: "Cancel the export",
      run: async () => {
        const answer = await services.post(`${JOBS_PATH}/${row.jobId}/cancel`, {}, (json) =>
          decodeExportJobRow(json),
        );
        if (answer === null) {
          return "The view changed; nothing was sent.";
        }
        upsert(answer);
        return answer.state === "CANCELLED"
          ? "Cancelled."
          : "Cancel requested. The job is not cancelled yet; it stops at its next chunk.";
      },
    });
  };

  const rowElement = (row: ExportJobRow): HTMLElement => {
    const view = jobPresentation(row);
    const cancel = view.cancellable
      ? h(
          "button",
          {
            attrs: {
              type: "button",
              "data-action": "cancel",
              "aria-label": `Cancel export ${row.jobId}`,
            },
            on: {
              click: (event) => {
                confirmCancel(row, event.currentTarget as HTMLElement);
              },
            },
          },
          "Cancel",
        )
      : null;
    const chunks = view.downloadable && row.artifact !== null ? row.artifact.chunks : 0;
    const downloads = Array.from({ length: chunks }, (_, index) =>
      h(
        "button",
        {
          attrs: {
            type: "button",
            "data-action": "download",
            "aria-label": `Download chunk ${String(index + 1)} of ${String(chunks)} of export ${row.jobId}`,
          },
          on: {
            click: () => {
              void download(row, index);
            },
          },
        },
        `Download ${String(index + 1)}/${String(chunks)}`,
      ),
    );
    return h(
      "tr",
      { attrs: { "data-job": row.jobId, "data-state": row.state } },
      h("th", { attrs: { scope: "row" } }, row.jobId.slice(0, 8)),
      h("td", {}, row.jobKind),
      h("td", {}, badge(view.label, view.tone), h("p", { class: "note" }, view.note)),
      h("td", {}, `${row.mode} / ${row.fidelity}`),
      h(
        "td",
        {},
        [
          row.workspaceId,
          row.accountId,
          row.sharedWorkspace === null ? null : `shared with ${row.sharedWorkspace}`,
        ]
          .filter((part): part is string => part !== null)
          .join(" / "),
      ),
      h("td", {}, instantCell(row.createdAt)),
      h("td", {}, instantCell(row.expiresAt)),
      h(
        "td",
        {},
        row.artifact === null
          ? metric(null)
          : `${formatQuantity(row.artifact.totalRows)} rows, ${formatQuantity(row.artifact.totalBytes)} bytes, ${String(row.artifact.chunks)} chunk(s)`,
      ),
      h("td", {}, cancel, ...downloads),
    );
  };

  function draw(): void {
    setChildren(list, [
      jobs.length === 0
        ? null
        : scrollRegion(
            "Report and export jobs",
            h(
              "table",
              { class: "grid" },
              h("caption", {}, "Report and export jobs"),
              h(
                "thead",
                {},
                h(
                  "tr",
                  {},
                  ...[
                    "Job",
                    "Kind",
                    "State",
                    "Mode / fidelity",
                    "Scope",
                    "Created",
                    "Expires",
                    "Artifact",
                    "Actions",
                  ].map((name) => h("th", { attrs: { scope: "col" } }, name)),
                ),
              ),
              h("tbody", {}, ...jobs.map(rowElement)),
            ),
          ),
    ]);
  }

  async function refresh(): Promise<void> {
    try {
      const answer = await ctx.client.read("exports", decodeExportJobRow);
      if (answer === null || ctx.scope.disposed) {
        return;
      }
      jobs = [...answer.page.rows];
      dataMode = answer.mode ?? "";
      const state = stateOfEnvelope(answer);
      setChildren(frame.banner, state.kind === "ready" ? [] : [renderStateBanner(state)]);
      setChildren(frame.freshness, [
        renderFreshnessBar(answer, { streamSequence: ctx.streamSequence.get() }),
      ]);
      draw();
    } catch (error) {
      setChildren(frame.banner, [renderStateBanner(stateOfError(error))]);
    }
  }

  const form = h("form", {
    class: "export-form",
    attrs: { "aria-label": "New export", novalidate: "" },
  });
  const problem = h("p", { class: "form-problem", attrs: { role: "alert" } });
  const period = select("period", "Review period", REVIEW_PERIODS, "WEEKLY");
  const submit = h("button", { attrs: { type: "submit" } }, "Review and create…");
  const buildForm = (): void => {
    setChildren(form, [
      select("kind", "Create", JOB_KINDS, "REPORT"),
      input("workspace", "Workspace", jobs[0]?.workspaceId ?? ""),
      input("shared", "Share with workspace (optional)", ""),
      select("mode", "Mode of the data", ["", ...MODES], dataMode),
      select("fidelity", "Fidelity of the data", ["", ...FIDELITIES], ""),
      period,
      submit,
      problem,
    ]);
  };
  const values = (): ExportForm => {
    const get = (name: string): string =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement).value;
    return {
      kind: get("kind"),
      workspace: get("workspace"),
      shared: get("shared"),
      mode: get("mode"),
      fidelity: get("fidelity"),
      period: get("period"),
    };
  };
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const current = values();
    const issue = formProblem(current);
    problem.textContent = issue ?? "";
    // The key is the intent's: the same body re-uses it (retry, refresh), a changed one gets its own.
    const request = issue === null ? buildExportRequest(current, ctx.accountId, "") : null;
    if (request === null) {
      return;
    }
    confirmWith(submit, {
      intentId: `export-create|${JSON.stringify(encodeExportRequest(request))}`,
      id: "create-export",
      title: "Create this export?",
      summary: [
        ["Kind", request.kind],
        ["Scope", [request.workspaceId, request.accountId ?? "whole workspace"].join(" / ")],
        ["Mode / fidelity", `${request.mode} / ${request.fidelity}`],
        ["Life", request.ttlSeconds === null ? "24 hours" : "1 hour"],
        [
          "Access",
          "Your grant and the data licence are checked now, again when it runs, and again at each download.",
        ],
      ],
      confirmLabel: "Create the export",
      run: async (clientKey) => {
        const answer = await services.post(
          JOBS_PATH,
          encodeExportRequest({ ...request, clientKey }),
          (json) => decodeExportJobRow(json),
        );
        if (answer === null) {
          return "The view changed; nothing was sent.";
        }
        upsert(answer);
        return `Queued as ${answer.jobId.slice(0, 8)}. It is not ready until it shows Succeeded.`;
      },
    });
  });

  ctx.scope.every(POLL_MS, () => {
    if (jobs.some((job) => LIVE.includes(job.state))) {
      void refresh();
    }
  });
  buildForm();
  frame.body.append(form, status, list);
  void refresh().then(buildForm);
  return handleOf(frame, ctx);
}
